import { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { useFleetApi, type BoundsRow } from "./api";

function agentRole(agent: string): "watcher" | "operator" | "specialist" {
  const a = agent.toLowerCase();
  if (a.includes("sentinel") || a.includes("watcher") || a.includes("drift") || a.includes("market") || a.includes("feed")) return "watcher";
  if (a.includes("detective") || a.includes("investigator")) return "specialist";
  return "operator";
}

function modeClass(mode: string | null | undefined): string {
  if (!mode) return "muted";
  if (mode === "autonomous") return "ok";
  if (mode === "supervised") return "warn";
  if (mode === "disabled") return "crit";
  return "muted";
}

export default function BoundsPolicy() {
  const { token, logout } = useAuth();
  const { fetchBounds } = useFleetApi(token, logout);

  const [rows, setRows] = useState<BoundsRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchBounds()
      .then(r => { if (alive) setRows(r); })
      .catch(e => { if (alive) setErr(String(e)); });
    return () => { alive = false; };
  }, [fetchBounds]);

  // Group by agent
  const byAgent: Record<string, BoundsRow[]> = {};
  if (rows) {
    for (const r of rows) {
      if (!byAgent[r.agent]) byAgent[r.agent] = [];
      byAgent[r.agent].push(r);
    }
  }

  const agents = Object.keys(byAgent).sort();

  return (
    <section>
      <span className="meta">configured in registry · widened/tightened automatically</span>
      <p className="page-lede">
        Each write tool has a declared bound. Out-of-bound attempts become escalations. Bounds widen one step after a clean window, tighten one step on a reversal or downstream complaint.
      </p>

      {err && <p style={{ color: "var(--crit)", fontSize: "12.5px" }}>{err}</p>}

      {rows && agents.length === 0 && (
        <p className="muted">No agent bounds registered yet.</p>
      )}

      {agents.map(agent => {
        const role = agentRole(agent);
        const agentRows = byAgent[agent];
        return (
          <div key={agent} className="card" style={{ marginBottom: "12px" }}>
            <h3>
              {agent.replace(/_/g, " ")}{" "}
              <span className={`pill ${role}`} style={{ fontSize: "10.5px" }}>{role}</span>
            </h3>
            <div
              className="bounds-row"
              style={{
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "var(--text-2)",
                fontWeight: 700,
              }}
            >
              <div>Situation class</div>
              <div>Decision class · mode</div>
              <div>Scope / since</div>
            </div>
            {agentRows.map((r, i) => {
              const tightened = r.reason?.toLowerCase().includes("tighten");
              return (
                <div key={i} className="bounds-row">
                  <div className="tool">{(r.situation_class || "—").replace(/_/g, " ")}</div>
                  <div className="bound">
                    <span className={`pill ${modeClass(r.mode)}`}>{r.mode ?? "—"}</span>
                    {" "}
                    <span style={{ color: "var(--text-2)", fontSize: "12px" }}>
                      {(r.decision_class || "").replace(/_/g, " ")}
                    </span>
                    {tightened && (
                      <span className="pill warn" style={{ marginLeft: "6px" }}>trimmed -1 step</span>
                    )}
                  </div>
                  <div className="scope">
                    {r.since ? new Date(r.since).toLocaleDateString("en-GB") : "—"}
                    {r.reason && (
                      <span style={{ display: "block", marginTop: "1px", color: "var(--text-3)" }}>
                        {r.reason}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </section>
  );
}
